/**
 * Offline Sync Service
 * Handles synchronization of offline data when connection is restored
 */

import { offlineStorageService, SyncQueueItem, OfflineWorkoutData } from './offlineStorageService';
import { hybridFirestoreService } from './hybridFirestoreService';
import { WorkoutLog, UserProfile } from '@/types/workout';

export interface SyncResult {
  success: boolean;
  syncedItems: number;
  failedItems: number;
  errors: string[];
}

export interface SyncStatus {
  isRunning: boolean;
  progress: number;
  currentItem?: string;
  lastSync?: Date;
  pendingItems: number;
}

class OfflineSyncService {
  private syncInProgress = false;
  private syncListeners: ((status: SyncStatus) => void)[] = [];
  private autoSyncEnabled = true;
  private syncInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.initializeAutoSync();
  }

  private initializeAutoSync(): void {
    // Listen for connection changes
    offlineStorageService.onConnectionChange((isOnline) => {
      if (isOnline && this.autoSyncEnabled) {
        this.syncAll();
      }
    });

    // Set up periodic sync when online
    this.syncInterval = setInterval(() => {
      if (offlineStorageService.isOnline() && this.autoSyncEnabled && !this.syncInProgress) {
        this.syncAll();
      }
    }, 30000); // Sync every 30 seconds when online
  }

  async syncAll(userId?: string): Promise<SyncResult> {
    if (this.syncInProgress) {
      throw new Error('Sync already in progress');
    }

    if (!offlineStorageService.isOnline()) {
      throw new Error('Cannot sync while offline');
    }

    this.syncInProgress = true;
    this.notifyListeners({ isRunning: true, progress: 0, pendingItems: 0 });

    const result: SyncResult = {
      success: true,
      syncedItems: 0,
      failedItems: 0,
      errors: []
    };

    try {
      // Get all pending sync items
      const syncQueue = await offlineStorageService.getSyncQueue();
      const userSyncItems = userId ? syncQueue.filter(item => item.data.userId === userId) : syncQueue;
      
      if (userSyncItems.length === 0) {
        this.notifyListeners({ 
          isRunning: false, 
          progress: 100, 
          pendingItems: 0,
          lastSync: new Date()
        });
        this.syncInProgress = false;
        return result;
      }

      this.notifyListeners({ 
        isRunning: true, 
        progress: 0, 
        pendingItems: userSyncItems.length 
      });

      // Process each sync item
      for (let i = 0; i < userSyncItems.length; i++) {
        const item = userSyncItems[i];
        const progress = Math.round(((i + 1) / userSyncItems.length) * 100);

        this.notifyListeners({
          isRunning: true,
          progress,
          currentItem: `Syncing ${item.type}...`,
          pendingItems: userSyncItems.length - i - 1
        });

        try {
          await this.syncItem(item);
          await offlineStorageService.removeFromSyncQueue(item.id);
          result.syncedItems++;
        } catch (error) {
          result.failedItems++;
          result.errors.push(`Failed to sync ${item.type}: ${error}`);

          // Increment retry count
          item.retryCount++;
          if (item.retryCount < item.maxRetries) {
            await offlineStorageService.addToSyncQueue(item);
          } else {
            // Max retries reached, remove from queue
            await offlineStorageService.removeFromSyncQueue(item.id);
            result.errors.push(`Max retries reached for ${item.type}, item removed from queue`);
          }
        }
      }

      // Sync offline workouts separately
      if (userId) {
        await this.syncOfflineWorkouts(userId, result);
      }

      result.success = result.failedItems === 0;

    } catch (error) {
      result.success = false;
      result.errors.push(`Sync failed: ${error}`);
    } finally {
      this.syncInProgress = false;
      this.notifyListeners({
        isRunning: false,
        progress: 100,
        pendingItems: result.failedItems,
        lastSync: new Date()
      });
    }

    return result;
  }

  private async syncItem(item: SyncQueueItem): Promise<void> {
    switch (item.type) {
      case 'workout':
        await this.syncWorkoutData(item.data);
        break;
      case 'profile':
        await this.syncProfileData(item.data);
        break;
      case 'progress':
        await this.syncProgressData(item.data);
        break;
      default:
        throw new Error(`Unknown sync item type: ${item.type}`);
    }
  }

  private async syncWorkoutData(workoutData: WorkoutLog): Promise<void> {
    // Convert WorkoutLog to WorkoutSession
    const workoutSession = {
      id: workoutData.sessionId,
      userId: workoutData.userId,
      week: workoutData.week,
      day: workoutData.day,
      session: workoutData.session,
      date: workoutData.startTime,
      exercises: [], // Convert from ExerciseLog to Exercise if needed
      completed: Boolean(workoutData.completedAt),
      completedAt: workoutData.completedAt,
      sessionNotes: workoutData.sessionNotes,
      duration: workoutData.endTime ?
        Math.round((workoutData.endTime.getTime() - workoutData.startTime.getTime()) / 60000) :
        undefined
    };

    await hybridFirestoreService.saveWorkoutSession(workoutData.userId, workoutSession);
  }

  private async syncProfileData(profileData: UserProfile): Promise<void> {
    await hybridFirestoreService.updateUserProfile(profileData.uid, profileData);
  }

  private async syncProgressData(progressData: any): Promise<void> {
    await hybridFirestoreService.saveWeeklyProgress(progressData.userId, progressData);
  }

  private async syncOfflineWorkouts(userId: string, result: SyncResult): Promise<void> {
    const offlineWorkouts = await offlineStorageService.getUnsyncedWorkouts(userId);
    
    for (const offlineWorkout of offlineWorkouts) {
      try {
        // Convert WorkoutLog to WorkoutSession
        const workoutSession = {
          id: offlineWorkout.workoutData.sessionId,
          userId: offlineWorkout.workoutData.userId,
          week: offlineWorkout.workoutData.week,
          day: offlineWorkout.workoutData.day,
          session: offlineWorkout.workoutData.session,
          date: offlineWorkout.workoutData.startTime,
          exercises: [], // Convert from ExerciseLog to Exercise if needed
          completed: Boolean(offlineWorkout.workoutData.completedAt),
          completedAt: offlineWorkout.workoutData.completedAt,
          sessionNotes: offlineWorkout.workoutData.sessionNotes,
          duration: offlineWorkout.workoutData.endTime ?
            Math.round((offlineWorkout.workoutData.endTime.getTime() - offlineWorkout.workoutData.startTime.getTime()) / 60000) :
            undefined
        };

        await hybridFirestoreService.saveWorkoutSession(userId, workoutSession);
        await offlineStorageService.markWorkoutSynced(offlineWorkout.id);
        result.syncedItems++;
      } catch (error) {
        result.failedItems++;
        result.errors.push(`Failed to sync offline workout: ${error}`);
        
        // Increment retry count
        offlineWorkout.retryCount++;
        if (offlineWorkout.retryCount >= 3) {
          // Mark as failed after 3 retries
          result.errors.push(`Max retries reached for offline workout ${offlineWorkout.id}`);
        }
      }
    }
  }

  async addToSyncQueue(type: SyncQueueItem['type'], data: any, maxRetries = 3): Promise<void> {
    const item: SyncQueueItem = {
      id: `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      data,
      timestamp: new Date(),
      retryCount: 0,
      maxRetries
    };

    await offlineStorageService.addToSyncQueue(item);

    // Try to sync immediately if online
    if (offlineStorageService.isOnline() && this.autoSyncEnabled && !this.syncInProgress) {
      setTimeout(() => this.syncAll(), 1000);
    }
  }

  async saveOfflineWorkout(userId: string, workoutData: WorkoutLog): Promise<void> {
    const offlineWorkout: OfflineWorkoutData = {
      id: `offline_workout_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId,
      workoutData,
      timestamp: new Date(),
      synced: false,
      retryCount: 0
    };

    await offlineStorageService.saveOfflineWorkout(offlineWorkout);

    // Try to sync immediately if online
    if (offlineStorageService.isOnline() && this.autoSyncEnabled && !this.syncInProgress) {
      setTimeout(() => this.syncAll(userId), 1000);
    }
  }

  async getPendingSyncCount(userId?: string): Promise<number> {
    const syncQueue = await offlineStorageService.getSyncQueue();
    const userQueue = userId ? syncQueue.filter(item => item.data.userId === userId) : syncQueue;
    
    const offlineWorkouts = userId 
      ? await offlineStorageService.getUnsyncedWorkouts(userId)
      : [];

    return userQueue.length + offlineWorkouts.length;
  }

  async getSyncStatus(): Promise<SyncStatus> {
    const pendingItems = await this.getPendingSyncCount();
    
    return {
      isRunning: this.syncInProgress,
      progress: this.syncInProgress ? 0 : 100,
      pendingItems,
      lastSync: undefined // Would need to store this
    };
  }

  onSyncStatusChange(callback: (status: SyncStatus) => void): () => void {
    this.syncListeners.push(callback);
    
    // Return cleanup function
    return () => {
      const index = this.syncListeners.indexOf(callback);
      if (index > -1) {
        this.syncListeners.splice(index, 1);
      }
    };
  }

  private notifyListeners(status: SyncStatus): void {
    this.syncListeners.forEach(callback => callback(status));
  }

  setAutoSyncEnabled(enabled: boolean): void {
    this.autoSyncEnabled = enabled;
  }

  isAutoSyncEnabled(): boolean {
    return this.autoSyncEnabled;
  }

  async forceSyncNow(userId?: string): Promise<SyncResult> {
    return this.syncAll(userId);
  }

  async clearSyncQueue(): Promise<void> {
    const syncQueue = await offlineStorageService.getSyncQueue();
    for (const item of syncQueue) {
      await offlineStorageService.removeFromSyncQueue(item.id);
    }
  }

  destroy(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    this.syncListeners = [];
  }
}

export const offlineSyncService = new OfflineSyncService();
